import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { toast } from 'sonner';
import { Activity, Bell, Calculator, Package2, RefreshCcw, ShieldCheck, Sparkles, Users } from 'lucide-react';

function metricCard({ label, value, icon: Icon, note }) {
  return (
    <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-xs uppercase tracking-wide text-slate-500 font-medium">{label}</p>
            <div className="mt-2 text-2xl font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>{value}</div>
            {note ? <p className="mt-1 text-xs text-slate-500">{note}</p> : null}
          </div>
          <div className="w-10 h-10 rounded-2xl bg-emerald-50 flex items-center justify-center text-emerald-700">
            <Icon size={18} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function formatDate(value) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}

export default function BudgetManagement() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({});
  const [activities, setActivities] = useState([]);
  const [activityUser, setActivityUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activityLoading, setActivityLoading] = useState(true);
  const [status, setStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);

  const load = async (nextPage = page) => {
    setRefreshing(true);
    try {
      const params = new URLSearchParams();
      if (status !== 'all') params.set('status', status);
      if (search.trim()) params.set('search', search.trim());
      params.set('page', String(nextPage));
      params.set('limit', '10');
      const r = await api.get(`/admin/budget/users?${params.toString()}`);
      setItems(r.data?.users || []);
      setSummary(r.data?.summary || {});
      setPage(r.data?.page || nextPage);
      setPages(r.data?.pages || 1);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load budget management');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const loadActivity = async (user = null) => {
    setActivityUser(user || null);
    setActivityLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('limit', '8');
      if (user?.id) params.set('user_id', user.id);
      const r = await api.get(`/admin/budget/activity?${params.toString()}`);
      setActivities(r.data?.activities || []);
    } catch (err) {
      setActivities([]);
    } finally {
      setActivityLoading(false);
    }
  };

  useEffect(() => {
    load(1);
    loadActivity();
  }, []);

  const doAction = async (url, payload, message) => {
    try {
      await api.post(url, payload);
      toast.success(message);
      await load(page);
      if (activityUser?.id && url.includes(`/admin/users/${activityUser.id}/`)) {
        await loadActivity(activityUser);
      } else {
        await loadActivity();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Action failed');
    }
  };

  const quickLinks = [
    { label: 'Budget Users', to: '/admin/budget-users', icon: Users },
    { label: 'Budget Packages', to: '/admin/budget-packages', icon: Package2 },
    { label: 'Budget Usage', to: '/admin/budget-usage', icon: Activity },
    { label: 'Budget Reports', to: '/admin/budget-reports', icon: Calculator },
    { label: 'Budget Alerts', to: '/admin/budget-alerts', icon: Bell },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-40">
        <div className="w-6 h-6 border-2 border-emerald-700 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold text-slate-900 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Budget Management</h1>
          <p className="text-slate-500 text-sm mt-0.5">Review Budget access, package assignment, trials and usage controls from one place.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button className="bg-emerald-700 hover:bg-emerald-800 text-white" onClick={async () => { await load(page); await loadActivity(); }} disabled={refreshing || activityLoading}>
            <RefreshCcw size={14} className={`mr-1.5 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {metricCard({ label: 'Budget Enabled', value: summary.budget_enabled_users ?? 0, icon: ShieldCheck, note: 'Users currently allowed to use Budget.' })}
        {metricCard({ label: 'Trials / Active', value: `${summary.trial_users ?? 0} / ${summary.active_users ?? 0}`, icon: Sparkles, note: 'Current trial and active subscriptions.' })}
        {metricCard({ label: 'Transactions', value: summary.transactions ?? 0, icon: Activity, note: 'Budget transaction count across users.' })}
        {metricCard({ label: 'Overdue', value: `${summary.overspending_users ?? 0} / ${summary.overdue_loans ?? 0}`, icon: Bell, note: 'Overspending users and overdue loans.' })}
      </div>

      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
        {quickLinks.map((item) => (
          <Button
            key={item.to}
            variant="outline"
            className="justify-start h-auto py-3 px-4 border-slate-200 bg-white"
            onClick={() => navigate(item.to)}
          >
            <item.icon size={16} className="mr-2 text-emerald-700" />
            {item.label}
          </Button>
        ))}
      </div>

      <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
        <CardHeader className="space-y-4">
          <CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>Budget Control Panel</CardTitle>
          <form
            className="flex flex-col gap-3 lg:flex-row lg:items-center"
            onSubmit={(event) => {
              event.preventDefault();
              setPage(1);
              load(1);
            }}
          >
            <div className="flex-1">
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name, email or phone" />
            </div>
            <div className="flex flex-wrap gap-2">
              {['all', 'active', 'trial', 'expired', 'cancelled', 'disabled'].map((item) => (
                <Button
                  key={item}
                  type="button"
                  variant={status === item ? 'default' : 'outline'}
                  className={status === item ? 'bg-emerald-700 hover:bg-emerald-800 text-white' : ''}
                  onClick={() => {
                    setStatus(item);
                    setPage(1);
                    setTimeout(() => load(1), 0);
                  }}
                >
                  {item === 'all' ? 'All' : item.charAt(0).toUpperCase() + item.slice(1)}
                </Button>
              ))}
            </div>
            <Button type="submit" className="bg-slate-900 hover:bg-slate-800 text-white">Search</Button>
          </form>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Package</TableHead>
                <TableHead>Budget</TableHead>
                <TableHead>Trial Ends</TableHead>
                <TableHead>Usage</TableHead>
                <TableHead>Audit</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((user) => (
                <TableRow key={user.id}>
                  <TableCell>
                    <div className="font-medium text-slate-900">{user.name}</div>
                    <div className="text-xs text-slate-500">{user.email || user.phone || user.id}</div>
                  </TableCell>
                  <TableCell>
                    <div className="font-medium text-slate-900">{user.package_name || 'Free'}</div>
                    <div className="text-xs text-slate-500">{user.subscription_status || '-'}</div>
                  </TableCell>
                  <TableCell>
                    <Badge className={user.budget_enabled ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-700'}>
                      {user.budget_enabled ? 'Enabled' : 'Disabled'}
                    </Badge>
                    <div className="text-xs text-slate-500 mt-1">{user.days_left > 0 ? `${user.days_left} days left` : 'No active budget trial'}</div>
                  </TableCell>
                  <TableCell>{formatDate(user.trial_end)}</TableCell>
                  <TableCell className="text-sm text-slate-600">
                    <div>Txns: {user.usage?.transactions ?? 0}</div>
                    <div>Branches: {user.usage?.branches ?? 0}</div>
                    <div>Loans: {user.usage?.loans ?? 0}</div>
                    <div>Credit: {user.usage?.credit ?? 0}</div>
                  </TableCell>
                  <TableCell>
                    <Button type="button" variant="outline" size="sm" title="View recent budget activity for this user" onClick={() => loadActivity(user)}>
                      View Activity
                    </Button>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-2">
                      <Button type="button" variant="outline" size="sm" onClick={() => navigate('/admin/budget-users')}>Open Users</Button>
                      <Button type="button" variant="outline" size="sm" onClick={() => navigate('/admin/budget-usage')}>Usage</Button>
                      <Button type="button" variant="outline" size="sm" onClick={() => {
                        const packageId = window.prompt('Enter package ID or code to assign Budget access:', user.package_name || '');
                        if (packageId) doAction(`/admin/users/${user.id}/package`, { package_id: packageId }, 'Package assigned');
                      }}>
                        Assign Package
                      </Button>
                      <Button type="button" variant="outline" size="sm" onClick={() => {
                        const days = Number(window.prompt('Extend trial by how many days?', '14') || 0);
                        if (days > 0) doAction(`/admin/users/${user.id}/subscription/trial`, { days }, 'Trial extended');
                      }}>
                        Extend Trial
                      </Button>
                      <Button type="button" variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => {
                        if (window.confirm(`Disable Budget access for ${user.name}? This will cancel the user's current subscription.`)) {
                          doAction(`/admin/users/${user.id}/subscription/cancel`, {}, 'Budget access disabled');
                        }
                      }}>
                        Disable Budget
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {!items.length && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-slate-500 py-10">
                    No budget users found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <div className="flex items-center justify-between text-sm text-slate-500">
        <span>Page {page} of {pages}</span>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => load(page - 1)}>Previous</Button>
          <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => load(page + 1)}>Next</Button>
        </div>
      </div>

      <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
        <CardHeader className="flex flex-row items-center justify-between gap-3">
          <div>
            <CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {activityUser ? `${activityUser.name}'s Budget Activity` : 'Recent Budget Activity'}
            </CardTitle>
            <p className="text-sm text-slate-500 mt-1">
              {activityUser
                ? 'Recent package, trial and subscription actions for this user.'
                : 'Recent package, trial and subscription actions are logged here for quick review.'}
            </p>
          </div>
          <div className="flex gap-2">
            {activityUser ? (
              <Button variant="outline" size="sm" onClick={() => loadActivity()}>
                Clear
              </Button>
            ) : null}
            <Button variant="outline" size="sm" onClick={() => loadActivity(activityUser)} disabled={activityLoading}>
            <RefreshCcw size={14} className={`mr-1.5 ${activityLoading ? 'animate-spin' : ''}`} /> Reload
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {activityLoading ? (
            <div className="text-sm text-slate-500">Loading recent activity...</div>
          ) : activities.length ? (
            activities.map((entry) => (
              <div key={entry.id} className="rounded-2xl border border-slate-200 p-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div className="space-y-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="font-medium text-slate-900">{entry.label || entry.action}</div>
                    <Badge className="bg-emerald-100 text-emerald-800 border-0">{entry.actor_name || 'Admin'}</Badge>
                    {entry.package_name ? <Badge variant="outline">{entry.package_name}</Badge> : null}
                  </div>
                  <div className="text-sm text-slate-600">
                    {entry.target_name ? (
                      <span>Target: {entry.target_name}</span>
                    ) : (
                      <span>Target: {entry.target_user_id || '-'}</span>
                    )}
                    {entry.days ? <span className="ml-3">Days: {entry.days}</span> : null}
                    {entry.subscription_status ? <span className="ml-3">Status: {entry.subscription_status}</span> : null}
                  </div>
                </div>
                <div className="text-sm text-slate-500">{formatDate(entry.created_at)}</div>
              </div>
            ))
          ) : (
            <div className="text-sm text-slate-500">
              {activityUser ? 'No recent budget activity found for this user.' : 'No recent budget activity yet.'}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
